import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

import { ReactionType } from './ReactionPicker';
import likeEmoji from '../assets/like-emoji.png';
import loveEmoji from '../assets/love-emoji.png';
import careEmoji from '../assets/care-emoji.png';
import hahaEmoji from '../assets/hilarious-emoji.png';
import wowEmoji from '../assets/wow-emoji.png';
import sadEmoji from '../assets/sad-emoji.png';

interface ReactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  totalCount: number;
  reactionCounts: Array<{
    reaction_type: ReactionType;
    count: number;
  }>;
}

const getReactionImage = (type: ReactionType) => {
  switch (type) {
    case 'like': return likeEmoji;
    case 'love': return loveEmoji;
    case 'care': return careEmoji;
    case 'haha': return hahaEmoji;
    case 'wow': return wowEmoji;
    case 'sad': return sadEmoji;
    default: return likeEmoji;
  }
};

const ReactionsModal = ({ isOpen, onClose, postId, totalCount, reactionCounts }: ReactionsModalProps) => {
  const [activeTab, setActiveTab] = useState('all');

  const getReactionLabel = (type: ReactionType) => {
    switch (type) {
      case 'like': return 'Like';
      case 'love': return 'Love';
      case 'care': return 'Care';
      case 'haha': return 'Haha';
      case 'wow': return 'Wow';
      case 'sad': return 'Sad';
      default: return type;
    }
  };

  const currentReactionData = activeTab === 'all' 
    ? reactionCounts 
    : reactionCounts.filter(r => r.reaction_type === activeTab);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Reactions {totalCount > 0 && `(${totalCount})`}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-auto gap-1 h-auto p-1">
            <TabsTrigger 
              value="all" 
              className="text-xs px-2 py-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              All
              {totalCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 text-xs">
                  {totalCount}
                </Badge>
              )}
            </TabsTrigger>
            {reactionCounts.map(({ reaction_type, count }) => (
              <TabsTrigger 
                key={reaction_type}
                value={reaction_type}
                className="text-xs px-2 py-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <img 
                  src={getReactionImage(reaction_type)} 
                  alt={reaction_type}
                  className="w-4 h-4 mr-1 object-contain"
                />
                <Badge variant="secondary" className="h-4 text-xs">
                  {count}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <ScrollArea className="h-80">
              <div className="space-y-4">
                {activeTab === 'all' ? (
                  reactionCounts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No reactions yet
                    </div>
                  ) : (
                    reactionCounts.map((reaction) => (
                      <div key={reaction.reaction_type} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={getReactionImage(reaction.reaction_type)} 
                            alt={reaction.reaction_type}
                            className="w-8 h-8 object-contain"
                          />
                          <div>
                            <p className="text-sm font-medium">
                              {getReactionLabel(reaction.reaction_type)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {reaction.count} {reaction.count === 1 ? 'person' : 'people'} reacted
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-sm font-medium">
                          {reaction.count}
                        </Badge>
                      </div>
                    ))
                  )
                ) : (
                  currentReactionData.map((reaction) => (
                    <div key={reaction.reaction_type} className="flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="text-center">
                        <img 
                          src={getReactionImage(reaction.reaction_type)} 
                          alt={reaction.reaction_type}
                          className="w-12 h-12 object-contain mx-auto mb-2"
                        />
                        <p className="text-lg font-medium">
                          {reaction.count} {getReactionLabel(reaction.reaction_type)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {reaction.count === 1 ? 'person reacted' : 'people reacted'}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ReactionsModal;